import type { IEventChannel } from "../model/Models";
import type { Stop,RoutingTime, TruckOptions } from "../model/Directions";
import { Map } from "../map"



export default class DirectionsModel {
    static readonly vehicleLoadTypes = { // todo i18n
        USHazmatClass1 : "US Explosives",
        USHazmatClass2 : "US Compressed gas",
        USHazmatClass3 : "US Flammable liquids",
        USHazmatClass4 : "US Flammable solids",
        USHazmatClass5 : "US Oxidisers",
        USHazmatClass6 : "US Poisons",
        USHazmatClass7 : "US Radioactive substances",
        USHazmatClass8 : "US Corrosive materials",
        USHazmatClass9 : "US Miscellaneous",
        otherHazmatExplosive : "Other Explosive",
        otherHazmatGeneral : "Other General",
        otherHazmatHarmfulToWater : "Other Harmful To Water"
    }

    private readonly eventChannel: IEventChannel;

    private routingTime: RoutingTime;
    private featureReferences: any[];
    private stops: Stop[];
    private waypointOrder: any[];
    private roundTrip: boolean;
    private optimized: boolean;
    private travelMode: string;
    private truckOptions: TruckOptions;
    private measurementSystem: string;
    private isEdit: boolean;

    constructor() {
        this.eventChannel = es.util.createEventChannel({
            "onModelChange" : 1,
            "onModelReady": 1,
        }, "es.control.Directions");

        this.routingTime = {
            defaultIdleTime: 0,
            departureTime: null,
            stopsIdleTime: []
        };

        this.featureReferences =[];
        this.stops = [];
        this.waypointOrder = [];
        this.roundTrip = true;
        this.optimized =  true;
        this.travelMode = "DRIVING";
        this.truckOptions = {
            vehicleWidth : 0,
            vehicleHeight: 0,
            vehicleLength: 0,
            vehicleWeight: 0,
            vehicleLoadType: "None"
        };
        this.measurementSystem = "IMPERIAL";
        this.isEdit = true;
    }

    /**
     * 
     * @param array 
     * @param arrayOrder 
     * 
     * @returns 
     */
    static getInOrder(array: any[], arrayOrder: number[]): any[] {
        const orderedArray = new Array(array.length);

        for (let i = 0; i < arrayOrder.length; i++) {
            orderedArray[i] = array[arrayOrder[i]];
        }
        
        if (array.length > arrayOrder.length) {
            orderedArray[orderedArray.length - 1] = array[array.length - 1];
        }

        return orderedArray;
    }

    /**
     * 
     * @param geom 
     * 
     * @returns 
     */
    private getPoint(geom: any[]): number[] {
        if (geom[0] === "MG") {
            geom = geom[1];
        }

        return [geom[1], geom[2]];
    }

    /**
     * 
     * @returns 
     */
    getEventChannel(): IEventChannel {
        return this.eventChannel
    }

    /**
     * 
     * @param map 
     */
    setMapStyles(map: Map) {
        const layersWithConditions = es.map.routing.getLayersConditionalStyle(map, this.stops, this.isRoundTrip(), !this.isEdit || !this.optimized);
        map.layer.list().forEach((layer) => {
            if (layer.getGeomType() === "POINT") {
                const id = layer.getId();
                const conditionStyles = layersWithConditions[id] ? layersWithConditions[id] : [];
                // TODO: should we always do this? setting the styler forces each layer to switch to client-side rendering
                const styleSettings = layer.getStyleSettingsClone();
                styleSettings.conditionalStyleSettings = {
                    conditionalStyles: conditionStyles,
                    name : "route"
                };

                layer.setStyleSettings(styleSettings);
            }
        });
    }

    /**
     * 
     * @param layerViewId 
     * @param removeLayerViewIdOnly 
     * 
     * @returns 
     */
    removeAllDatasetStops(layerViewId: number, removeLayerViewIdOnly: boolean): boolean {
        const matchingStops = this.stops.filter((s) => s.layerViewId === layerViewId);

        if (removeLayerViewIdOnly) {
            matchingStops.forEach((stop) => {
                delete stop.layerViewId; // as this layerViewId no longer exists
                stop.invalid = true; // so that we tell the view that the route is broken
            });
        } else {
            this.stops = this.stops.filter((stop) => !matchingStops.includes(stop));
        }

        this.eventChannel.fireEvent("onModelChange");

        return matchingStops.length > 0;
    }

    /**
     * 
     * @param newRoutingTime 
     * @param order 
     * 
     * @returns 
     */
    overrideTime(newRoutingTime: RoutingTime, order: any[]) {
        this.waypointOrder = order;
        this.routingTime = newRoutingTime;

        const departureTime = parseFloat(newRoutingTime.departureTime);
        const date = new Date(departureTime);

        const showDepartureTime = newRoutingTime.departureTime && date.toISOString().substring(11,16) !== "00:00"; // we don't show departuretime on the results for "00:00" time

        newRoutingTime.departureTime = newRoutingTime.departureTime ? departureTime : null;

        if (this.stops.length === 0) {
            return;
        }


        for (let i = 0; i < order.length; i++) {
            const stop = this.stops[i];

            if (stop.idleTime) {
                es.log.warn("overriding existing idleTime !");
            }

            stop.idleTime = this.routingTime.stopsIdleTime[order[i]] || 0;
            // idleTime is in minutes, whereas the computed value is in seconds.
            stop.duration.withIdleTime = showDepartureTime ? stop.duration.value : stop.duration.value + (stop.idleTime * 60);
        }

    }

    /**
     * 
     */
    setStopStartAndFinishTimes() {
        var departureTime = parseFloat(this.routingTime.departureTime);
        var date = new Date(departureTime);
        var showDepartureTime = this.routingTime.departureTime && date.toISOString().substring(11,16) !== "00:00"; // we don't show departuretime on the results for "00:00" time

        this.stops.forEach((stop) => {
            if (showDepartureTime) {
                stop.startTime = es.util.date.date2Str(departureTime, true).substring(11,16);
                if (stop.idleTime){
                    departureTime = departureTime + (stop.idleTime * 60 * 1000); // time is in miliseconds
                    stop.finishTime = es.util.date.date2Str(departureTime, true).substring(11,16);
                }
                departureTime = departureTime + (stop.duration.value  * 1000 ); // add duration value for the next stop start time
            }
        });
    }

    /**
     * 
     * @returns 
     */
    getTotalTime(): number {
        return this.stops.reduce(function(total, stop) {
            return total + stop.duration.value + (stop.idleTime * 60 );
        }, 0);
    }

    /**
     * 
     * @returns 
     */
    getTotalDistance(): number {
        return this.stops.reduce(function(total, stop) {
            return total + stop.distance.value;
        }, 0);
    }

    /**
     * 
     * @returns 
     */
    getStops(): Stop[] {
        return this.stops.slice();
    }

    /**
     * 
     * @returns 
     */
    getWaypointOrder(): any[] {
        return this.waypointOrder;
    }

    stopsSize(): number {
        return this.stops.length;
    }

    /**
     * 
     * @param stop 
     * @param index 
     */
    addStop(stop: Stop, index = -1) {
        if (!stop.idleTime) {
            stop.idleTime = this.routingTime.defaultIdleTime;
        }

        if (index > -1) {
            this.stops.splice(0, 0, stop);
        } else {
            this.stops.push(stop);
        }

        this.eventChannel.fireEvent("onModelChange");
    }

    /**
     * 
     * @param from 
     * @param to 
     */
    moveStop(from: number, to: number) {
        const stop = this.stops.splice(from, 1)[0];
        this.stops.splice(to, 0, stop);
    }

    /**
     * 
     * @param id 
     */
    removeStop(id: number) {
        this.stops.splice(id, 1);
        this.eventChannel.fireEvent("onModelChange");
    }

    /**
     * 
     * @param id 
     * @param stop 
     */
    updateStop(id: number, stop: Stop) {
        this.stops[id] = stop;
        this.eventChannel.fireEvent("onModelChange");
    }

    /**
     * 
     * @returns 
     */
    getFeatureReferences() {
        return this.featureReferences;
    }

    /**
     * 
     * @returns 
     */
    isOptimized(): boolean {
        return this.optimized;
    }

    /**
     * 
     * @returns 
     */
    isRoundTrip(): boolean {
        return this.roundTrip;
    }

    /**
     * 
     * @param isRoundTrip 
     */
    setRoundTrip(isRoundTrip: boolean) {
        this.roundTrip = isRoundTrip;
        this.eventChannel.fireEvent("onModelChange");
    }

    /**
     * 
     * @param isOptimized 
     */
    setOptimized(isOptimized: boolean) {
        this.optimized = isOptimized;
        this.eventChannel.fireEvent("onModelChange");
    }

    /**
     * 
     * @returns 
     */
    getTravelMode(): string {
        return this.travelMode;
    }

    /**
     * 
     * @param newTravelMode 
     */
    setTravelMode(newTravelMode: string) {
        this.travelMode = newTravelMode;
    }

    /**
     * 
     * @returns 
     */
    getTruckOptions(): TruckOptions {
        return this.truckOptions;
    }

    /**
     * 
     * @param newTruckOptions 
     */
    setTruckOptions(newTruckOptions: TruckOptions) {
        this.truckOptions = newTruckOptions;
        
        // Cleanse data
        this.truckOptions.vehicleWidth = parseFloat(this.truckOptions.vehicleWidth) ? parseFloat(this.truckOptions.vehicleWidth) : 0;
        this.truckOptions.vehicleHeight = parseFloat(this.truckOptions.vehicleHeight) ? parseFloat(this.truckOptions.vehicleHeight) : 0;
        this.truckOptions.vehicleLength = parseFloat(this.truckOptions.vehicleLength) ? parseFloat(this.truckOptions.vehicleLength) : 0;
        this.truckOptions.vehicleWeight = parseFloat(this.truckOptions.vehicleWeight) ? parseFloat(this.truckOptions.vehicleWeight) : 0;
    }

    /**
     * 
     * @param defaultIdleTime 
     * @param departureTime 
     */
    setRoutingTime(defaultIdleTime: number, departureTime?: Date) {
        const oldDefaultIdleTime = this.routingTime.defaultIdleTime;
        
        this.routingTime.defaultIdleTime = defaultIdleTime;
        this.routingTime.departureTime =  departureTime ? departureTime.getTime() : null;
        
        this.stops.forEach((stop) => {
            if (stop.idleTime == undefined || stop.idleTime == oldDefaultIdleTime) {
                stop.idleTime = defaultIdleTime;
            }
        });
    }

    /**
     * 
     * @param defaultIdleTime 
     */
    setDefaultIdleTime(defaultIdleTime: number) {
        const oldDefaultIdleTime = this.routingTime.defaultIdleTime;

        this.routingTime.defaultIdleTime = defaultIdleTime;

        this.stops.forEach((stop) => {
            if (stop.idleTime == undefined || stop.idleTime == oldDefaultIdleTime) {
                stop.idleTime = defaultIdleTime;
            }
        });
    }

    /**
     * 
     * @returns 
     */
    getDefaultIdleTime(): number {
        return this.routingTime.defaultIdleTime;
    }

    /**
     * 
     * @returns 
     */
    getDepartureTime(): number | null | undefined {
        return this.routingTime.departureTime;
    }

    /**
     * 
     * @param val 
     */
    setDepartureTime(val: number) {
        this.routingTime.departureTime = val;
    }

    /**
     * 
     * @param newStops 
     */
    setStops(newStops: any[]) {
        this.stops = newStops;
        this.eventChannel.fireEvent("onModelChange");
    }

    /**
     * 
     * @param val 
     */
    setMeasurementSystem(val: string) {
        this.measurementSystem = val;
    }

    /**
     * 
     * @returns 
     */
    getMeasurementSystem(): string {
        return this.measurementSystem;
    }

    /**
     * 
     * @param stop 
     * 
     * @returns 
     */
    getStopIndex(stop: Stop): number {
        return this.stops.findIndex(s => s.layerViewId == stop.layerViewId && s.id == stop.id);
    }

    /**
     * 
     * @returns 
     */
    getModel() {
        return {
            featureReferences: this.featureReferences,
            stops: this.stops,
            routingTime: this.routingTime,
            roundTrip: this.roundTrip,
            optimized: this.optimized,
            travelMode : this.travelMode,
            truckOptions : this.truckOptions
        };
    }

    /**
     * 
     */
    sortStopsFromStart() {
        const startStop = this.stops[0];
        const startCoords = startStop.geom.slice(1, 3) as [number, number];
        
        // Add field to store the distance from the stop to the start coordinates
        const finishIdx = this.isRoundTrip() ? this.stops.length : this.stops.length - 1;
        const stopsToSort = []
        for (let i = 1; i < finishIdx; i++) {
            const feature = this.stops[i];
            const stopCoords = feature.geom.slice(1, 3) as [number, number];
            const distance = es.util.math.distSq(...startCoords, ...stopCoords);
            feature._distanceFromStart = distance;
            stopsToSort.push(feature);
        }

        stopsToSort.sort((a, b) => a._distanceFromStart - b._distanceFromStart);
        const updatedStopsOrder = stopsToSort;
        updatedStopsOrder.unshift(startStop);
        if (!this.isRoundTrip()) {
            updatedStopsOrder.push(this.stops[this.stops.length-1]);
        }

        this.stops = updatedStopsOrder;
    }

    /**
     * 
     * @param map 
     * 
     * @returns 
     */
    buildRequest(map) {
        this.routingTime.stopsIdleTime = this.stops.map((stop) => stop.idleTime || 0);
        const requestRoutingTime = es.util.object.deepCopy(this.routingTime);
        requestRoutingTime.departureTime = requestRoutingTime.departureTime ? requestRoutingTime.departureTime : undefined;// server expects nothing instead of null

        const request = {
            avoidFerries: true,
            avoidHighways: false,
            avoidTolls: false,
            destination: {"x": 0, "y": 0},
            language: "en",
            routingTime: es.util.object.deepCopy(requestRoutingTime),
            optimizeWaypoints: this.isOptimized(),
            origin: {"x": 0, "y": 0},
            region: "us", // TODO: this shouldn't be hardcoded!
            travelMode: this.travelMode,
            truckOptions : this.truckOptions,
            measurementSystem: this.getMeasurementSystem(),
            roundTrip: this.isRoundTrip()
        };

        let location = this.getPoint(this.stops[0].geom);
        request.origin.x = location[0];
        request.origin.y = location[1];
        let finish = this.stops.length;
        if (this.isRoundTrip()) {
            request.destination.x = request.origin.x;
            request.destination.y = request.origin.y;
        } else {
            location = this.getPoint(this.stops[this.stops.length - 1].geom);
            request.destination.x = location[0];
            request.destination.y = location[1];
            finish = this.stops.length - 1;
        }

        const waypoints = [];
        const start = 1; // stop 0 is origin so it is not a waypoint
        for (let i = start; i < finish; i++) {
            location = this.getPoint(this.stops[i].geom);
            const waypoint = {
                x: location[0],
                y: location[1]
            };
            waypoints.push(waypoint);
        }
        request.waypoints = waypoints; 

        return request;
    }

    /**
     * 
     * @param val 
     */
    setEdit(val: boolean) {
        this.isEdit = val;
        this.eventChannel.fireEvent("onModelChange");
    }

    /**
     * 
     * @returns 
     */
    isEditMode(): boolean {
        return this.isEdit;
    }

    /**
     * 
     * @param routingResponse 
     */
    fromRoutingResponse(routingResponse: any) {
        const routingResponseCopy = es.util.object.deepCopy(routingResponse);
        if(routingResponseCopy['featureReferences']) {
            es.log.info("TOR9-21309: fromRoutingResponse setting featureReferences, count=" + routingResponseCopy.featureReferences.length, routingResponseCopy.featureReferences);
            this.featureReferences = routingResponseCopy.featureReferences;
        } else {
            es.log.warn("TOR9-21309: fromRoutingResponse - No routingResponse.featureReferences!", routingResponse);
        }
        this.setStops(routingResponseCopy.routes[0].legs);
        this.waypointOrder = routingResponse.routes[0].waypointOrder;
        this.roundTrip = routingResponse.routingRequest.roundTrip;
        this.optimized = routingResponse.routingRequest.optimizeWaypoints;
        this.travelMode = routingResponse.routingRequest.travelMode;
        this.truckOptions = routingResponse.routingRequest.truckOptions
        this.measurementSystem = routingResponse.routingRequest.measurementSystem;
        this.isEdit = false;
        this.overrideTime(routingResponse.routingRequest.routingTime, routingResponse.routes[0].waypointOrder);
    }
}
